import React, { useContext, useEffect, useState } from 'react';
import ChildNameHeading from 'components/SiblingSwitch/ChildNameHeading';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import { toast } from 'react-toastify';

import DocumentsContentContainer from 'components/Documents/DocumentsContentContainer';
import { Page } from 'components/Page';
import {
  downloadUploadedDocumentAPI,
  getPatientUploadedDocumentsAPI,
  PatientUploadedDocument,
} from 'services/documentServices';
import { useAppDispatch } from 'store/hooks';
import { getDocumentList, downloadDocument, getStaffUploadedDocuments } from 'store/slices/documentSlice';
import { AuthContext } from 'utils/AuthContext';
import { formatDateTime } from 'utils/dataFormatter';

const Documents = () => {
  const dispatch = useAppDispatch();
  const { user } = useContext(AuthContext);

  const [filesData, setFilesData] = useState<any[]>([]);
  const [providerDocuments, setProviderDocuments] = useState<any[]>([]);
  const [uploadedHistory, setUploadedHistory] = useState<PatientUploadedDocument[]>([]);
  const [isUploadedDocumentsLoading, setIsUploadedDocumentsLoading] = useState(false);
  const [uploadRefreshKey, setUploadRefreshKey] = useState(0);

  const patientId = user?.patientId ? String(user.patientId) : '';
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<number | null>(null);

  useEffect(() => {
    if (!user?.patientId) return;

    dispatch(getDocumentList(user.patientId))
      .unwrap()
      .then((res: any) => {
        setFilesData(res?.data?.documentInfos ?? []);
      });

    dispatch(getStaffUploadedDocuments(user.patientId))
      .unwrap()
      .then((res: any) => {
        setProviderDocuments(res ?? []);
      });
  }, [user?.patientId, dispatch]);

  useEffect(() => {
    if (!user?.patientId) {
      setUploadedHistory([]);
      return;
    }

    let isMounted = true;
    setIsUploadedDocumentsLoading(true);

    getPatientUploadedDocumentsAPI(user.patientId)
      .then((documents) => {
        if (isMounted) {
          setUploadedHistory(documents ?? []);
        }
      })
      .catch(() => {
        if (isMounted) {
          setUploadedHistory([]);
          toast('Unable to load uploaded documents. Please try again.', { type: 'error' });
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsUploadedDocumentsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [user?.patientId, uploadRefreshKey]);

  const pendingDocs = filesData.filter((d) => d.status !== 'Completed');
  const completedDocs = filesData.filter((d) => d.status === 'Completed');

  const handleDownload = async (name: string) => {
    try {
      const res = await dispatch(downloadDocument(user?.patientId ?? '')).unwrap();
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${res.data}`;
      link.download = name;
      link.click();
    } catch (err) {
      console.error(err);
      toast('Something went wrong. Please contact the front office through Secure Chat to request the document.', {
        type: 'error',
      });
    }
  };

  const handleDownloadDocument = async (documentId: number) => {
    setDownloadingDocumentId(documentId);
    try {
      await downloadUploadedDocumentAPI(Number(patientId), documentId, '');
    } catch (error) {
      console.error('Document download failed:', downloadingDocumentId, error);
      toast('Download failed. Please try again.', { type: 'error' });
    } finally {
      setDownloadingDocumentId(null);
    }
  };

  const handleUploadSuccess = () => {
    setUploadRefreshKey((key) => key + 1);
  };

  return (
    <Page title={<ChildNameHeading title='Documents' />}>
      <Box sx={{ p: 3 }}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>Upload Documents</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <DocumentsContentContainer patientId={patientId} onUploadSuccess={handleUploadSuccess} />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>Your Uploaded Documents</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {isUploadedDocumentsLoading && (
              <Stack alignItems='center' sx={{ py: 2 }}>
                <CircularProgress size={28} />
              </Stack>
            )}
            {!isUploadedDocumentsLoading &&
              uploadedHistory.map((doc) => (
                <Card key={doc.documentId} sx={{ mb: 2 }}>
                  <CardContent>
                    <Stack direction='row' justifyContent='space-between' alignItems='center'>
                      <Box>
                        <Typography>{doc.documentName}</Typography>
                        {doc.documentDescription && (
                          <Typography variant='body2' color='text.secondary'>
                            {doc.documentDescription}
                          </Typography>
                        )}
                        <Typography variant='caption'>{formatDateTime(doc.uploadedDate)}</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            {!isUploadedDocumentsLoading && uploadedHistory.length === 0 && (
              <Alert severity='info'>No uploaded documents found.</Alert>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>Documents from Your Provider</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {providerDocuments.map((doc) => (
              <Card key={doc.documentId} sx={{ mb: 2 }}>
                <CardContent>
                  <Stack direction='row' justifyContent='space-between' alignItems='center'>
                    <Box>
                      <Typography>{doc.documentName}</Typography>
                      <Typography variant='caption'>{doc.uploadedDate}</Typography>
                    </Box>
                    <IconButton onClick={() => handleDownloadDocument(doc.documentId)}>
                      <FileDownloadOutlinedIcon />
                    </IconButton>
                  </Stack>
                </CardContent>
              </Card>
            ))}
            {providerDocuments.length === 0 && (
              <Alert severity='info'>No documents available from your provider.</Alert>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>Treatment Consents</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {pendingDocs.length !== 0 ? (
              <Alert severity='info'>
                We have emailed you our policies and treatment consents. Check your inbox for a message from
                &quot;MindWeal via Docusign&quot;, review the documents, and sign as needed. Then, return to the app to
                complete the onboarding steps.
              </Alert>
            ) : (
              pendingDocs.map((doc) => (
                <Card key={doc.documentId} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography fontWeight={600}>{doc.documentName}</Typography>
                    <Chip label='Sent for Signature' color='warning' size='small' sx={{ mt: 1 }} />
                  </CardContent>
                </Card>
              ))
            )}

            {completedDocs.length > 0 && (
              <>
                {completedDocs.map((doc) => (
                  <Card key={doc.documentId} sx={{ mb: 2 }}>
                    <CardContent>
                      <Stack direction='row' justifyContent='space-between' alignItems='center'>
                        <Typography>Signed Treatment Consents</Typography>
                        <Stack direction='row' spacing={1}>
                          <IconButton onClick={() => handleDownload(doc.documentName)}>
                            <FileDownloadOutlinedIcon />
                          </IconButton>
                          <TaskAltOutlinedIcon color='success' />
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
    </Page>
  );
};

export default Documents;
